import * as React from "react";
import {Fragment} from "react";

import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import {IMessageProps, MapProvider} from "./~types/MessageProps";
import {MessageType} from "../../~store/models/messages/enums/MessageType";
import {
    botTextToComponentWithLinks,
    clientMessageTextToComponentWithLinks
} from "src/app/shared/helpers/weblinks-helper/WeblinksHelper";
import {getTime} from "src/app/shared/helpers/date-helper/DateFormats";
import { CUSTOM_BUTTONS_STYLE_TAG } from "src/app/chat/~store/models/Constants"
import "./Message.css";
import {GET_FILE_API_ROUTE, GET_PICTURE_API_ROUTE} from "../../~api/apiRoutes";
import { IFile, MessageContentType, OperatorType} from "../../~store/models/messages/Message";
import i18n from "src/app/shared/localization/i18n";
import { ILocation } from "../../~store/models/Location";

const Message = (props: IMessageProps) => {
    const _getMessageClassName = () => {
        switch(props.message.messageType) {
            case MessageType.ClientMessage:
                return "client_message";
            case MessageType.OperatorMessage:
            case MessageType.ClientSystemMessage:
                return "operator_message";
            default: return "";
        }
    }

    const _getMessageStyle = () => {
        return {
            backgroundImage: 'url(' + GET_PICTURE_API_ROUTE + props.message.operatorModel.avatarId + "&customerId=" + props.customerId + ')',
            backgroundRepeat: 'no-repeat'
        }
    }
    
    const getOperatorImageUrl = () => GET_PICTURE_API_ROUTE + props.message.operatorModel.avatarId + "&customerId=" + props.customerId;

    const _getBackgroundStyle = () => {
        return props.message.operatorModel?.avatarId ? {
            backgroundImage: 'none'
        } : {}
    }

    const mapFileFromMessage = (file: IFile) => {
        if(file === null || file === undefined || file.fileId === null) {return;}
        return (
            <div><a href={GET_FILE_API_ROUTE + file.fileId +"&customerId=" + props.customerId} target="_blank">{file.name}</a></div>
        )
    }

    const convertMessageTextToContent = () => {
        switch (props.message.contentType) {
            case MessageContentType.Markup:
                return botTextToComponentWithLinks(props.message.text);
            case MessageContentType.Text:
                return clientMessageTextToComponentWithLinks(props.message.text);
            case MessageContentType.Video:
                return (
                    <video
                        className="message-item__media"
                        src={props.message.text}
                        autoPlay={false}
                        loop={false}
                        preload="none"
                        controls={true}
                    />
                );
            case MessageContentType.Audio:
                return (
                    <audio
                        src={props.message.text}
                        controls
                    />
                );
            case MessageContentType.TextToSpeech:
                return (
                    <>
                        {botTextToComponentWithLinks(props.message.text)}
                        <i className="material-icons replay-text-to-speech-button" onClick={_onReplayTextToSpeech}>
                            replay
                        </i>
                    </>
                );
            case MessageContentType.Navigation:
                return clientMessageTextToComponentWithLinks(props.message.text);
            default:
                return props.message.operatorModel?.type === OperatorType.Bot ? botTextToComponentWithLinks(props.message.text) : clientMessageTextToComponentWithLinks(props.message.text);
        }
    }

    const _onReplayTextToSpeech = (): void => {
        window.speechSynthesis.cancel();
        const msg = new SpeechSynthesisUtterance();
        msg.text = props.message.text;
        msg.lang = 'ru-RU';
        msg.onend = () => {window.speechSynthesis.cancel()};
        window.speechSynthesis.speak(msg);
    }

    const getName =() => {
        switch(props.message.messageType) {
            case MessageType.ClientMessage:
                return i18n.t("chat.you");
            case MessageType.OperatorMessage:
            case MessageType.ClientSystemMessage:
                return props.message.operatorModel?.displayName ? props.message.operatorModel.displayName : i18n.t("chat.operator");
            default: return i18n.t("chat.system");
        }
    }

    const urlParams = new URLSearchParams(window.location.href);
    let operatorPosition = urlParams.get("inline_operator_position");
    operatorPosition = operatorPosition == null ? "undefined": operatorPosition;
    const additionalInfo = urlParams.get("separeteOperatorNameAndTimeSent") === "true" ? true : false;
    const separateMessageAndButtons = urlParams.get("separate_message_and_buttons") === "true";
    const dateFormat = urlParams.get("setDateTimeFormat") ?? "";
    
    const getFooterDiv = (includeLine: boolean) => {
        return (
            <Fragment>
            <div className="message__operator_content">
                    {props.message.messageType == MessageType.OperatorMessage && <img className="message__operator_image" src={getOperatorImageUrl()} alt="Operator avatar" />}
                    <Typography className={`message__inline_${(props.message.messageType == MessageType.ClientMessage ? "right" : "left")}_info`} variant="subtitle2">{getName()}, {getTime(props.message.timeSent, dateFormat)}</Typography>
            </div>
                {includeLine && <hr/>}
            </Fragment>
        );
    }
    
    const getMessageButtonClassName = (isButtonsDisabled: boolean) => {

        let className = 'message__button';

        if (isButtonsDisabled) {
            className += ' button__disabled';
        }

        if (props.message.tag?.includes(CUSTOM_BUTTONS_STYLE_TAG)) {
            className += ' custom_buttons';
        }

        return className;
    }

    const getMapUrl = (location: ILocation) =>
    {
        if (props.mapProvider == MapProvider.Yandex)
        {
            return "https://yandex.ru/map-widget/v1/?ll=" + location?.longitude + "," + location?.latitude + "&z=15&pt=" + location?.longitude + "," + location?.latitude + ",pm2rdm";
        }
        else
        {
            return "https://maps.google.com/maps?q=" + location?.latitude + "," + location?.longitude + "&z=15&output=embed";
        }
    }

    const getButtons = () => {
        return <div className="message__buttons_container" id={`message_${props.message.externalId}_buttons`}>
            {props.message.buttons ? props.message.buttons.map((button, index) => {
                    let buttonRef = React.createRef<HTMLDivElement>();

                    return (
                    <div
                        ref={buttonRef}
                        className={getMessageButtonClassName(props.isButtonsDisabled)}
                        key={index}
                        onClick={() =>
                        {
                            if(!props.isButtonsDisabled) {
                                buttonRef.current!.className = getMessageButtonClassName(true);
                                setTimeout((button: HTMLDivElement) => {
                                    button.className = getMessageButtonClassName(false);
                                    props.setForceButtonDisabled(false);
                                }, 1000, buttonRef.current);
                            }

                            props.onMenuButtonClick(button);
                            
                            if(!props.isButtonsDisabled) 
                                props.setForceButtonDisabled(true);
                        }}>
                        {button.text}
                    </div>)})
                : ""}
        </div>;
    }

    return (
        <div className={`${_getMessageClassName()} ${(operatorPosition === "undefined" ? "": "no_background_image")}`}  id={`message_${props.message.externalId}`} style={_getBackgroundStyle()}>
            {props.message.operatorModel?.avatarId && operatorPosition === "undefined" && <div className={`${_getMessageClassName()}_avatar message_avatar`} style={_getMessageStyle()}></div>}
            <Paper className="message__content">
                {additionalInfo && operatorPosition === "undefined" && <div className={`${_getMessageClassName()}_info`}>{getName()}, {getTime(props.message.timeSent, dateFormat)}</div>}
                {!additionalInfo && operatorPosition === "undefined" && props.message.operatorModel?.displayName && <div><div>{props.message.operatorModel.displayName}</div><hr /></div>}
                {operatorPosition === "top" && getFooterDiv(true)}
                {props.message.imageId !== null && props.message.imageId !== undefined &&
                    <a href={GET_PICTURE_API_ROUTE + props.message.imageId + "&customerId=" + props.customerId} target="_blank">
                        <img className="message__image" src={GET_PICTURE_API_ROUTE + props.message.imageId + "&customerId=" + props.customerId} /></a>}
                {props.message.location !== null && props.message.location !== undefined &&
                    <iframe className="location_iframe" src={getMapUrl(props.message.location)} /> || convertMessageTextToContent()}
                {mapFileFromMessage(props.message.file)}
                {!separateMessageAndButtons && getButtons()}                
                {!additionalInfo && operatorPosition === "undefined" && <Typography className="message__created-date" variant="subtitle2" gutterBottom={true} align="right">
                    {getTime(props.message.timeSent, dateFormat)}
                </Typography>}
            </Paper>
            {separateMessageAndButtons && getButtons()}
            {operatorPosition === "bottom" && getFooterDiv(false)}
        </div>
    );
}

export default Message;
